import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * All supported Oracle Fusion sync types.
 * The handler dispatches to the appropriate sync logic based on this value.
 */
export const FUSION_SYNC_TYPES = [
  "products",
  "warehouses",
  "uom",
  "customers",
  "locations",
  "customer_accounts",
  "price_lists",
  "transaction_codes",
  "sales_product_types",
  "freight_terms",
  "carriers",
  "suppliers",
  "addresses",
] as const;

export type FusionSyncType = (typeof FUSION_SYNC_TYPES)[number];

export const FUSION_SYNC_TYPE_LABELS: Record<FusionSyncType, string> = {
  products: "Products",
  warehouses: "Warehouses",
  uom: "Units of Measure",
  customers: "Customers (deprecated — use customer_accounts)",
  locations: "Locations (deprecated — use customer_accounts)",
  customer_accounts: "Customer Accounts & Locations",
  price_lists: "Price Lists",
  transaction_codes: "Transaction Codes (Order Types)",
  sales_product_types: "Sales Product Types",
  freight_terms: "Freight Terms",
  carriers: "Carriers",
  suppliers: "Suppliers",
  addresses: "Addresses (Party Sites)",
};

export const fusionSyncStep = defineStepConfig({
  type: "fusion_sync",
  name: "Sync from Oracle Fusion",
  description:
    "Fetches data from the Oracle Fusion REST API and syncs it into the local database. Use the sync type dropdown to select which entity to sync.",
  category: "Data Access",

  inputSchema: z.object({}),

  outputSchema: z.object({
    totalFetched: z.number(),
    succeeded: z.number(),
    failed: z.number(),
    syncTime: z.string(),
    isDelta: z.boolean(),
  }),

  configSchema: z
    .object({
      syncType: z.enum(FUSION_SYNC_TYPES),
      fullSyncOnFirstRun: z.boolean().prefault(true),
      forceFullSync: z.boolean().prefault(false),
      initialConcurrency: z.number().int().positive().prefault(5),
      maxConcurrency: z.number().int().positive().prefault(10),
      increaseFactor: z.number().positive().min(1).prefault(2),
      decreaseFactor: z.number().positive().max(1).prefault(0.5),
      maxRetries: z.number().int().nonnegative().prefault(3),
    })
    .refine((c) => c.initialConcurrency <= c.maxConcurrency, {
      message: "initialConcurrency must be ≤ maxConcurrency",
      path: ["initialConcurrency"],
    }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 minutes",
  },
});
